import { useQuery } from '@tanstack/react-query';
import { parseHttpError } from '@upme/api-client';
import type { AdminEventDto } from '@upme/contracts';
import { Button } from '@upme/ui/components/button';
import { toast } from 'sonner';
import { roleErrorText } from '../../users/lib/role-error-text';
import { cityModeratorsOptions } from '../../users/queries';
import { useSetEventManager } from '../mutations';

interface Props {
  event: AdminEventDto;
  isSuperadmin: boolean;
}

function label(u: { username: string | null; firstName: string | null; id: string }): string {
  return u.username ? `@${u.username}` : (u.firstName ?? u.id);
}

/**
 * Закреплённый менеджер события (#166) — CITY_MODERATOR города события.
 * Назначает только SUPERADMIN; актуальность роли менеджера не гарантируется (сервер её не чистит).
 */
export function EventManagerCard({ event, isSuperadmin }: Props) {
  const moderators = useQuery({ ...cityModeratorsOptions(), enabled: isSuperadmin });
  const setManager = useSetEventManager(event.id);

  const candidates = (moderators.data?.items ?? []).filter(
    (u) => u.moderatedCityIds.includes(event.cityId) && u.id !== event.manager?.id,
  );

  async function assign(managerUserId: string | null) {
    try {
      await setManager.mutateAsync(managerUserId);
      toast.success(managerUserId ? 'Менеджер назначен' : 'Менеджер снят');
    } catch (err) {
      const apiErr = await parseHttpError(err);
      toast.error(roleErrorText(apiErr?.message, 'Не удалось изменить менеджера'));
    }
  }

  return (
    <section className="rounded-lg border bg-card p-4 space-y-3">
      <h2 className="text-sm font-semibold">Менеджер</h2>

      <div className="flex items-center gap-3 text-sm">
        <span>{event.manager ? label(event.manager) : 'не назначен'}</span>
        {isSuperadmin && event.manager && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={setManager.isPending}
            onClick={() => void assign(null)}
          >
            Снять
          </Button>
        )}
      </div>

      {isSuperadmin && moderators.isError && (
        <p className="text-xs text-muted-foreground">Не удалось загрузить менеджеров</p>
      )}

      {isSuperadmin &&
        moderators.isSuccess &&
        (candidates.length > 0 ? (
          <ul className="space-y-1 text-sm">
            {candidates.map((u) => (
              <li key={u.id} className="flex items-center gap-3">
                <span>{label(u)}</span>
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  aria-label={`Назначить ${label(u)}`}
                  disabled={setManager.isPending}
                  onClick={() => void assign(u.id)}
                >
                  Назначить
                </Button>
              </li>
            ))}
          </ul>
        ) : !event.manager ? (
          <p className="text-xs text-muted-foreground">
            В городе события нет менеджеров — выдайте роль в карточке пользователя
          </p>
        ) : null)}
    </section>
  );
}
